import { ACCOUNT_NAME_MSG, DEFAULT_INDUSTRY, MSG, PASSWORD_MSG, SignupRequest, oauthErrorText, type Industry, type Me, type SignupResponse } from "@genbaryoku/contract";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, ApiError, messageOf } from "../api.js";
import { setMe } from "../auth.js";
import { ClosedNotice } from "../components/ClosedNotice.jsx";
import { OAuthButtons } from "../components/OAuthButtons.jsx";
import { AuthCard, ErrorText, Field, Help, PasswordInput, PasswordRules, guardComposingEnter, useFieldIds } from "../components/ui.jsx";
import { AddCompanyPanel } from "../core/auth/AddCompany.jsx";
import { IndustrySelect } from "../components/IndustrySelect.jsx";
import { useProduct } from "../lib/useProduct.js";

export function SignupPage() {
  const ids = useFieldIds(["family", "given", "mail", "pw", "industry"] as const);
  // 業種（#142 段4）：はじめから製品の既定が選ばれた形。Google・Apple で登録するときも同じ業種で会社を作る
  const P = useProduct();
  const [industry, setIndustry] = useState<Industry>(DEFAULT_INDUSTRY[P.product]);
  const nav = useNavigate();
  const qc = useQueryClient();
  const [serverError, setServerError] = useState<string | undefined>();
  // Google・Apple から戻って断られた（#112）
  const [sp] = useSearchParams();
  const oauthError = oauthErrorText(sp.get("oauth_error")) || undefined;
  const [closedEmail, setClosedEmail] = useState<string | undefined>();
  /** E0-c：もう確認済みのアカウントがあり、この製品の会社はまだ無い（パスワードを聞いて会社を作る） */
  const [existsEmail, setExistsEmail] = useState<string | undefined>();
  // 姓・名（#152、docs/61 ★2）：一番上に1行に2つ。どちらも必須（★a）
  const form = useForm<SignupRequest>({ resolver: zodResolver(SignupRequest), defaultValues: { familyName: "", givenName: "", email: "", password: "" }, shouldFocusError: true });
  const fe = form.formState.errors;
  const firstError = fe.familyName?.message ?? fe.givenName?.message ?? fe.email?.message ?? fe.password?.message;
  // 強さのバー（API と同じ物差し）。メールアドレスの一部が入っていないかも見る
  const pwValue = form.watch("password") ?? "";
  const emailValue = form.watch("email") ?? "";

  const submit = form.handleSubmit(async (v) => {
    setServerError(undefined);
    try {
      const r = await api<SignupResponse | Me>("POST", "/auth/signup", { ...v, industry });
      // 海外の IP（#110）：確認コードの窓で確かめ終えると、確認済みでログインした Me が返る
      if ("user" in r) {
        await setMe(qc, r);
        nav("/home", { replace: true });
        return;
      }
      if (r.exists) {
        setExistsEmail(v.email);
        return;
      }
      nav("/signup/sent", { state: { email: v.email } });
    } catch (e) {
      if (e instanceof ApiError && e.code === "AUTH_ACCOUNT_CLOSED") {
        setClosedEmail(v.email);
        return;
      }
      if (e instanceof ApiError && e.problem.errors) {
        const errs = e.problem.errors;
        if (errs.familyName) form.setError("familyName", { message: errs.familyName });
        if (errs.givenName) form.setError("givenName", { message: errs.givenName });
        if (errs.email) form.setError("email", { message: errs.email });
        if (errs.password) form.setError("password", { message: errs.password });
        return;
      }
      setServerError(messageOf(e));
    }
  });

  if (existsEmail) {
    return (
      <AuthCard notices title="会員登録（無料）" mode="signup">
        <AddCompanyPanel mode="signup" email={existsEmail} industry={industry} name={{ familyName: form.getValues("familyName"), givenName: form.getValues("givenName") }} onBack={() => setExistsEmail(undefined)} />
      </AuthCard>
    );
  }
  return (
    <AuthCard notices title="会員登録（無料）" onSubmit={submit} mode="signup" help={<Help testId="help-signup" text={`聞くのは氏名・メールアドレス・パスワード・業種だけ。氏名は全部の会社で同じです。会社名や住所は、はじめて見積を出すときにお聞きします。業種に合わせて、分類と品目の見本が入ります（あとで消せます）。パスワードは、${PASSWORD_MSG.tip}`} />}>
      {closedEmail ? <ClosedNotice email={closedEmail} purgeAfter={null} /> : null}
      <div className="namerow" data-testid="signup-name">
        <Field id={ids.family} label={ACCOUNT_NAME_MSG.family}>
          <input id={ids.family} className="inp" autoComplete="family-name" maxLength={50} aria-invalid={fe.familyName ? true : undefined} onKeyDown={guardComposingEnter} {...form.register("familyName")} />
        </Field>
        <Field id={ids.given} label={ACCOUNT_NAME_MSG.given}>
          <input id={ids.given} className="inp" autoComplete="given-name" maxLength={50} aria-invalid={fe.givenName ? true : undefined} onKeyDown={guardComposingEnter} {...form.register("givenName")} />
        </Field>
      </div>
      <Field id={ids.mail} label="メールアドレス" help={<Help testId="help-signup-mail" text="確認のメールをお送りします。お客様には出ません。" />}>
        <input id={ids.mail} className="inp" type="email" inputMode="email" autoComplete="username" onKeyDown={guardComposingEnter} {...form.register("email")} />
      </Field>
      <Field id={ids.pw} label="パスワード（8文字以上）">
        <PasswordInput id={ids.pw} autoComplete="new-password" reg={form.register("password")} />
        <PasswordRules value={pwValue} email={emailValue} />
      </Field>
      <IndustrySelect id={ids.industry} product={P.product} value={industry} onChange={setIndustry} />
      <ErrorText>{serverError ?? firstError ?? oauthError}</ErrorText>
      <button type="submit" className="btn primary big" disabled={form.formState.isSubmitting}>
        無料ではじめる
      </button>
      {/* 同意の文（#120）：「利用規約」「プライバシーポリシー」の2つがリンク */}
      <p className="hint consent" data-testid="signup-consent">
        <a href="/terms" target="_blank" rel="noopener noreferrer">{MSG.footerTerms}</a>・<a href="/privacy" target="_blank" rel="noopener noreferrer">{MSG.footerPrivacy}</a>
        {MSG.signupConsentTail}
      </p>
      {/* Google・Apple で登録（#112）。みなし同意の一文はどのボタンにもかかる（上の一文） */}
      <OAuthButtons intent="signup" industry={industry} />
      {/* 「すでにお持ちの方は」＋文字のボタン「ログイン」を中央で、縦の中心をそろえる（#120） */}
      <div className="authlinks center">
        <span>
          {MSG.signupHaveAccount}
          <Link className="btn text" to="/login">ログイン</Link>
        </span>
      </div>
    </AuthCard>
  );
}
